<script lang="ts">
import { onMount, setContext, tick, untrack } from 'svelte';
import Footer from '../stories/organisms/Footer/Footer.svelte';
import Header from '../stories/organisms/Header/Header.svelte';

// GdprBanner event detail types
interface GdprConsentEvent {
    detail: {
        agreed: boolean;
    };
}

// Type for GDPR Banner component instance
interface GdprBannerInstance {
    show: () => void;
    hide: () => void;
}

import '../scss/yui-reset-3.18.1-min.scss';
import '../scss/_base.scss';
import '../scss/_builder.io.scss';

import { register, setEditorSettings } from '@builder.io/sdk-svelte';
import { page } from '$app/state';
import SEO from '$lib/components/SEO/index.svelte';
// Analytics services are lazy-loaded after GDPR consent to reduce initial bundle size
// See initAnalytics() function below for dynamic imports
import { trackEvent, trackPageview } from '$lib/utils/analytics.js';
import { isBuilderPreviewHost } from '$lib/utils/builderPreviewHost';
import { CUSTOM_COMPONENTS } from '../CustomComponents.js';
import {
    HUBSPOT_TRACKING_ID,
    LINKEDIN_PARTNER_ID,
    META_PIXEL_ID,
    OPENAI_ADS_PIXEL_ID,
} from '../config/apiKeys.js';
// Contact component will be dynamically imported
import { isContactOpen, isMenuOpen } from '../utils/store.js';

// Dynamic imports for better code splitting
let ContactComponent: typeof import('../stories/organisms/Contact/Contact.svelte').default | null =
    $state(null);
let loadingContact = $state(false);

async function loadContact() {
    if (!ContactComponent && !loadingContact) {
        loadingContact = true;
        try {
            const module = await import('../stories/organisms/Contact/Contact.svelte');
            if (module?.default) {
                ContactComponent = module.default;
            } else {
                console.error('Contact component module.default is undefined');
                ContactComponent = null;
            }
        } catch (error) {
            console.error('Failed to load Contact component:', error);
            ContactComponent = null;
        } finally {
            loadingContact = false;
        }
    }
    return ContactComponent;
}

import { browser } from '$app/environment';
import { afterNavigate, goto, onNavigate } from '$app/navigation';
import { SITE_URL } from '../config/constants';
import { ERoutes } from '../config/enums';

// Load contact component when contact modal is opened
let contactModalOpen = $state(false);

// Sync store with reactive state
isContactOpen.subscribe((value) => {
    contactModalOpen = value;
});

$effect(() => {
    if (contactModalOpen && !ContactComponent && !loadingContact) {
        loadContact();
    }
});

// Monitor URL hash for #contact (handles direct navigation, bookmarks, browser back/forward)
// Note: The handleLinkClicks function below prevents hash from being added when clicking links,
// so this primarily handles direct URL navigation and browser history
$effect(() => {
    if (page.url.hash === '#contact' && !contactModalOpen) {
        isContactOpen.set(true);

        // Clean URL by removing hash using SvelteKit's goto (updates page.url properly)
        // Note: Analytics tracking happens in the isContactOpen subscription below
        goto(window.location.pathname + window.location.search, {
            replaceState: true,
            noScroll: true,
        });
    }
});

let metaPixelEnabled = $state(false);
let metaPixelInitialized = $state(false);
let linkedInEnabled = $state(false);
let linkedInInitialized = $state(false);
let gdprBanner: GdprBannerInstance | undefined = $state();
let GdprBannerComponent: typeof import('@beyonk/gdpr-cookie-consent-banner').default | null =
    $state(null);
let gdprBannerLoading: Promise<typeof import('@beyonk/gdpr-cookie-consent-banner').default> | null =
    null;
let analyticsReady = false;
let initialNavigationHandled = false;
const landingPagePath = browser ? window.location.pathname + window.location.search : '';
const shouldSuppressGdprBanner = browser && isBuilderPreviewHost(window.location.hostname);

async function loadGdprBanner() {
    if (GdprBannerComponent) {
        return GdprBannerComponent;
    }

    if (!gdprBannerLoading) {
        gdprBannerLoading = Promise.all([
            import('@beyonk/gdpr-cookie-consent-banner'),
            import('@beyonk/gdpr-cookie-consent-banner/banner.css'),
            import('../scss/_gdpr-cookie-consent-banner.scss'),
        ]).then(([module]) => module.default);
    }

    GdprBannerComponent = await gdprBannerLoading;
    return GdprBannerComponent;
}

async function initAnalytics(event: GdprConsentEvent) {
    if (event.detail.agreed) {
        try {
            // Lazy-load analytics services only after user consents
            // This defers loading the analytics libraries until needed
            const [ga4Module, postHogModule, hubSpotModule] = await Promise.all([
                import('$lib/services/ga4.js'),
                import('$lib/services/posthog.js'),
                import('$lib/services/hubspot.js'),
            ]);

            // Initialise GA4 with Google Consent Mode v2 support
            const ga4Initialized = ga4Module.initGA4Service({
                debug: false,
            });

            if (!ga4Initialized) {
                console.error('[Analytics] Failed to initialize GA4');
            }

            // Update Google Consent Mode v2 - grant all consent when user accepts
            ga4Module.updateConsent({
                ad_storage: 'granted',
                ad_user_data: 'granted',
                ad_personalization: 'granted',
                analytics_storage: 'granted',
            });

            // Initialise PostHog with same consent as Google Analytics
            postHogModule.initPostHog({
                debug: false,
                enableSessionRecording: true,
            });

            // Initialise HubSpot tracking with same consent
            if (HUBSPOT_TRACKING_ID) {
                hubSpotModule.initHubSpot({
                    trackingId: HUBSPOT_TRACKING_ID,
                    debug: false,
                });
            } else {
                console.warn(
                    '[Analytics] HubSpot tracking ID not configured. Add it to src/config/apiKeys.js',
                );
            }

            analyticsReady = true;

            // Track the page the user is currently on.
            // For new visitors: captures the landing page after consent.
            // For returning visitors: captures the landing page after PostHog inits.
            // Skip GA4 if still on the landing page — the auto pageview (send_page_view: true
            // in app.html) already covers it. This prevents double-counting in GA4.
            const currentPath = window.location.pathname + window.location.search;
            const stillOnLandingPage = currentPath === landingPagePath;
            trackPageview(undefined, {}, { skipGA4: stillOnLandingPage });
        } catch (error) {
            console.error('[Analytics] Failed to initialize analytics services:', error);
        }
    }
}
/**
 * Initialize advertising pixels after user consent.
 * Triggered by the GDPR banner's "tracking" event when the user updates tracking-cookie consent.
 */
function initTracking(event: GdprConsentEvent) {
    if (!browser) return;

    void import('$lib/services/openai-ads.js')
        .then((openAIAdsModule) => {
            openAIAdsModule.setOpenAIAdsTrackingConsent(event.detail.agreed);

            if (event.detail.agreed) {
                openAIAdsModule.initOpenAIAdsPixel({
                    pixelId: OPENAI_ADS_PIXEL_ID,
                    debug: false,
                });
                openAIAdsModule.trackOpenAIPageViewed();
            }
        })
        .catch((error) => {
            console.error('[Analytics] Failed to initialise OpenAI Ads Pixel:', error);
        });

    if (event.detail.agreed) {
        metaPixelEnabled = true;

        // Only initialize if not already done
        if (!metaPixelInitialized) {
            try {
                // Validate configuration
                if (!META_PIXEL_ID) {
                    console.warn(
                        '[Analytics] Meta Pixel ID not configured. Add it to src/config/apiKeys.js',
                    );
                    return;
                }

                // Load Meta Pixel script
                ((f: Window, b: Document, e: 'script', v: string) => {
                    if (f.fbq) return;
                    // Regular function required for arguments keyword (Meta Pixel's official initialization code)
                    // biome-ignore lint/suspicious/noAssignInExpressions: Meta Pixel's official initialization pattern
                    const n = (f.fbq = ((...args: unknown[]) => {
                        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
                    }) as facebook.Pixel.Event);
                    if (!f._fbq) f._fbq = n;
                    n.push = n as unknown as (...args: unknown[]) => void;
                    n.loaded = true;
                    n.version = '2.0';
                    n.queue = [];
                    const t = b.createElement(e);
                    t.async = true;
                    t.src = v;
                    const s = b.getElementsByTagName(e)[0];
                    s?.parentNode?.insertBefore(t, s);
                })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

                // Initialize with Pixel ID from configuration
                window.fbq?.('init', META_PIXEL_ID);
                window.fbq?.('track', 'PageView');

                metaPixelInitialized = true;
                console.log('[Meta Pixel] Tracking initialised successfully');
            } catch (error) {
                console.error('[Analytics] Failed to initialise Meta Pixel:', error);
                metaPixelEnabled = false;
            }
        }

        // Initialize LinkedIn Insight Tag
        if (!linkedInInitialized) {
            try {
                if (!LINKEDIN_PARTNER_ID) {
                    console.warn(
                        '[Analytics] LinkedIn Partner ID not configured. Add it to src/config/apiKeys.js',
                    );
                    return;
                }

                window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
                window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);

                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.async = true;
                script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
                const firstScript = document.getElementsByTagName('script')[0];
                firstScript?.parentNode?.insertBefore(script, firstScript);

                linkedInInitialized = true;
                linkedInEnabled = true;
                console.log('[LinkedIn] Insight Tag initialised successfully');
            } catch (error) {
                console.error('[Analytics] Failed to initialise LinkedIn Insight Tag:', error);
                linkedInEnabled = false;
            }
        }
    }
}

/**
 * set builder.io options
 */
setEditorSettings({ customInsertMenu: true });
register('insertMenu', {
    default: true,
    name: 'Skywire components',
    items: CUSTOM_COMPONENTS.map((component) => ({
        name: component.name,
    })),
});

//page transitions
onNavigate((navigation) => {
    if (!document.startViewTransition) return;

    return new Promise((resolve) => {
        document.startViewTransition(async () => {
            resolve();
            await navigation.complete;
        });
    });
});

// biome-ignore lint/suspicious/noExplicitAny: Data type from +layout.server.js, children is Svelte snippet
let { data, children }: { data: any; children: any } = $props();

//write all page data to store for menu
setContext(
    'menuPages',
    untrack(() => data?.navPages),
);
setContext(
    'theServices',
    untrack(() => data?.serviceNames),
);

isMenuOpen.set(false);

function updateBackgroundColour(shouldUpdateBackgroundColour: boolean) {
    if (browser) {
        if (shouldUpdateBackgroundColour) {
            document?.querySelector('meta[name=theme-color]')?.setAttribute('content', '#eeff42');
        } else {
            document?.querySelector('meta[name=theme-color]')?.setAttribute('content', '#fff');
        }
    }
}

isMenuOpen.subscribe((value) => {
    updateBackgroundColour(value);

    // Track menu toggle (only in browser)
    if (browser) {
        const eventName = value ? 'menu_opened' : 'menu_closed';
        const eventProps = {
            page_url: window.location.href,
        };

        trackEvent(eventName, eventProps);
    }
});

isContactOpen.subscribe((value) => {
    updateBackgroundColour(value);

    // Track contact modal opens (only in browser)
    if (value && browser) {
        const eventProps = {
            page_url: window.location.href,
            page_title: document.title,
        };

        trackEvent('contact_modal_opened', eventProps);
    }
});

setContext('LayoutContext', {
    menuIsOpen: isMenuOpen,
    websiteLoaderActive: false,
    animateLoader: false,
    showLoader: false,
    alwaysDarkHeaderIcons: false,
});

afterNavigate(() => {
    initAosIfNeeded();

    // Track Meta Pixel ViewContent event on navigation
    if (metaPixelEnabled && window.fbq) {
        window.fbq?.('track', 'ViewContent');
    }

    // Only track if analytics is already initialised.
    // The initial pageview is handled by initAnalytics() to avoid
    // the race condition where afterNavigate fires before PostHog is ready.
    if (analyticsReady) {
        trackPageview();
    }

    // The current page is measured when tracking consent is initialised.
    // Measure only later client-side navigations here to avoid counting it twice.
    if (initialNavigationHandled) {
        void import('$lib/services/openai-ads.js')
            .then((openAIAdsModule) => {
                openAIAdsModule.trackOpenAIPageViewed();
            })
            .catch((error) => {
                console.error('[Analytics] Failed to measure OpenAI Ads page view:', error);
            });
    } else {
        initialNavigationHandled = true;
    }
});

const openCookieBanner = async (e: Event) => {
    e.preventDefault();
    if (shouldSuppressGdprBanner) {
        return;
    }

    await loadGdprBanner();
    await tick();
    gdprBanner?.show();
};

let aosInitialised = false;

const initAosIfNeeded = () => {
    if (!browser || aosInitialised || !document.querySelector('[data-aos]')) {
        return;
    }

    Promise.all([import('aos'), import('aos/dist/aos.css')])
        .then(([AOS]) => {
            AOS.default.init({
                duration: 300, // values from 0 to 3000, with step 50ms
                offset: 10, // offset (in px) from the original trigger point
                once: true, // whether animation should happen only once - while scrolling down
            });
            aosInitialised = true;
        })
        .catch((error) => {
            console.error('Failed to load AOS:', error);
        });
};

const aosInit = (_node: HTMLElement) => {
    queueMicrotask(initAosIfNeeded);
};

const entityMeta = {
    url: `${SITE_URL}/favicon.svg`,
    faviconWidth: 512,
    faviconHeight: 512,
    //caption: author,
};

let seoProps = $derived({
    title: page.data.meta.title,
    slug: page.data.meta.slug,
    ogImage: page.data.meta.ogImage,
    entityMeta,
    datePublished: '2024-02-23T14:19:33.000+0100',
    lastUpdated: '2024-02-23T14:19:33.000+0100',
    breadcrumbs: page.data.meta.breadcrumbs ?? [
        {
            name: 'Home',
            slug: '',
        },
    ],
    metaDescription: page.data.meta.description,
});

/**
 * Intercept clicks on #contact links to open flyout smoothly without hash navigation.
 * This prevents the page from jumping and provides a smoother UX.
 * Separate $effect above handles direct URL navigation with #contact hash.
 * @param event
 */
const handleLinkClicks = (event: MouseEvent) => {
    const target = event.target as HTMLAnchorElement;
    if (target.href?.includes('#contact')) {
        event.preventDefault();
        isContactOpen.set(true);
    }
};

/**
 * Handle keyboard events for accessibility
 * @param event
 */
const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
        const target = event.target as HTMLAnchorElement;
        if (target.href?.includes('#contact')) {
            event.preventDefault();
            isContactOpen.set(true);
        }
    }
};

// Set up global event listeners for better accessibility
onMount(() => {
    if (!shouldSuppressGdprBanner) {
        void loadGdprBanner();
    }

    document.addEventListener('click', handleLinkClicks);
    document.addEventListener('keydown', handleKeydown);

    return () => {
        document.removeEventListener('click', handleLinkClicks);
        document.removeEventListener('keydown', handleKeydown);
    };
});
</script>

<svelte:window />
<SEO {...seoProps} />

<Header
    altNavColour={page.data?.builderProps?.content?.data
        ?.useBlackNavTextColour || page.error?.message}
    altButtonColour={page.data?.builderProps?.content?.data
        ?.useBlackButtonTextColour ||
        page.data?.useBlackButtonTextColour ||
        page.error?.message}
/>

<main
    class={page.data?.builderProps?.model}
    use:aosInit
>
    {@render children?.()}

    <!-- https://github.com/beyonk-group/gdpr-cookie-consent-banner -->
    {#if !shouldSuppressGdprBanner && GdprBannerComponent}
        <GdprBannerComponent
            bind:this={gdprBanner}
            cookieName="beyonk_gdpr"
            heading=""
            description={`We use cookies to improve your experience on our website. By clicking accept, you consent to our <a href="${ERoutes.Privacy}">privacy policy & use of cookies</a>.`}
            on:analytics={initAnalytics}
            on:tracking={initTracking}
            choices={{
                necessary: {
                    label: 'Necessary cookies',
                    description: "Used for cookie control. Can't be turned off.",
                    value: true,
                },
                tracking: {
                    label: 'Tracking cookies',
                    description:
                        'Used to control Meta Pixel, LinkedIn Insight Tag, and OpenAI Ads conversion measurement, for advertising purposes.',
                    value: true,
                },
                analytics: {
                    label: 'Analytics cookies',
                    description:
                        'Used to control Google Analytics, PostHog, and HubSpot, which are 3rd party tools used to track user behaviour and improve our services.',
                    value: true,
                },
                marketing: false,
            }}
            showEditIcon={false}
        />
    {/if}
</main>
{#key page.url}
    {#if $isContactOpen}
        {#if loadingContact}
            <!-- Loading state while Contact component loads -->
            <div class="contact-loading">Loading...</div>
        {:else if ContactComponent}
            {@const ContactComp = ContactComponent}
            <ContactComp
                title={data?.contactUs?.data?.title}
                introText={data?.contactUs?.data?.introText}
                successTitle={data?.contactUs?.data?.successTitle}
                successMessage={data?.contactUs?.data?.successMessage}
                submitButtonText={data?.contactUs?.data?.submitButtonText}
                privacyTextBeforeLink={data?.contactUs?.data?.privacyTextBeforeLink}
                privacyLinkText={data?.contactUs?.data?.privacyLinkText}
                privacyTextAfterLink={data?.contactUs?.data?.privacyTextAfterLink}
            />
        {/if}
    {/if}
{/key}

<Footer
    skywireMenu={data?.footer?.data?.skywireLinks}
    cookieOnClick={openCookieBanner}
    newsletterTitle={data?.footer?.data?.newsletterTitle}
    newsletterText={data?.footer?.data?.newsletterText}
/>

<style lang="scss">
    @keyframes fade-in {
        from {
            opacity: 0;
        }
    }

    @keyframes fade-out {
        to {
            opacity: 0;
        }
    }

    @keyframes slide-from-right {
        from {
            transform: translateX(30px);
        }
    }

    @keyframes slide-to-left {
        to {
            transform: translateX(-30px);
        }
    }

    :root::view-transition-old(root) {
        animation:
            90ms cubic-bezier(0.4, 0, 1, 1) both fade-out,
            300ms cubic-bezier(0.4, 0, 0.2, 1) both slide-to-left;
    }

    :root::view-transition-new(root) {
        animation:
            210ms cubic-bezier(0, 0, 0.2, 1) 90ms both fade-in,
            300ms cubic-bezier(0.4, 0, 0.2, 1) both slide-from-right;
    }

    :global(.contact-loading) {
        position: fixed;
        top: 50%;
        right: 2rem;
        background: var(--colour-pickled-pineapple);
        padding: 2rem;
        border-radius: 0.5rem;
        transform: translateY(-50%);
        z-index: 15;
    }
</style>
